/** Account-scoped favorite lists shared by the mate hubs and the home shortcuts. */

export type DashboardFavorite = { service: string; toolId: string };

export type SyncedList<T> = {
  items: T[];
  /** Cloud is missing or missing ids that only this browser still has. */
  writeCloud: boolean;
  /** Legacy unscoped cache was folded in and should be removed after a successful sync. */
  clearLegacy: boolean;
};

export function dedupePreserveOrder<T>(items: T[], key: (item: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const id = key(item);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(item);
  }
  return out;
}

export function parseStringIdList(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  return dedupePreserveOrder(
    raw.filter((item): item is string => typeof item === "string" && item.length > 0),
    (id) => id,
  );
}

export function parseDashboardFavorites(raw: unknown): DashboardFavorite[] | null {
  if (!Array.isArray(raw)) return null;
  const entries: DashboardFavorite[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const service = (item as { service?: unknown }).service;
    const toolId = (item as { toolId?: unknown }).toolId;
    if (typeof service !== "string" || typeof toolId !== "string" || !service || !toolId) continue;
    entries.push({ service, toolId });
  }
  return dedupePreserveOrder(entries, (entry) => `${entry.service}:${entry.toolId}`);
}

/**
 * Cloud wins once this account has a browser cache.
 * Until then, union the legacy device cache so existing stars survive the first login.
 * `null` means the store has no value yet. An empty array is a real saved value.
 */
export function resolveSyncedList<T>(input: {
  cloud: T[] | null;
  legacy: T[] | null;
  account: T[] | null;
  key: (item: T) => string;
}): SyncedList<T> {
  const key = input.key;
  const cloud = input.cloud === null ? null : dedupePreserveOrder(input.cloud, key);
  const legacy = input.legacy === null ? null : dedupePreserveOrder(input.legacy, key);
  const account = input.account === null ? null : dedupePreserveOrder(input.account, key);
  const signature = (items: T[]) => items.map(key).join("\n");

  if (cloud === null && account === null) {
    const items = legacy ?? [];
    return {
      items,
      writeCloud: items.length > 0,
      clearLegacy: items.length > 0,
    };
  }

  if (cloud === null) {
    const items = account ?? [];
    return {
      items,
      writeCloud: items.length > 0,
      clearLegacy: (legacy?.length ?? 0) > 0,
    };
  }

  if (account === null) {
    const items = dedupePreserveOrder([...cloud, ...(legacy ?? [])], key);
    return {
      items,
      writeCloud: signature(items) !== signature(cloud),
      clearLegacy: (legacy?.length ?? 0) > 0,
    };
  }

  return { items: cloud, writeCloud: false, clearLegacy: false };
}

export function toggleId(ids: string[], id: string): string[] {
  if (!id) return ids;
  if (ids.includes(id)) return ids.filter((item) => item !== id);
  return [...ids, id];
}

/** Keep ids the user just ordered, then any hidden ids that were not in that list. */
export function reorderIds(current: string[], ordered: string[]): string[] {
  return dedupePreserveOrder(
    [...ordered, ...current.filter((id) => !ordered.includes(id))],
    (id) => id,
  );
}

/**
 * Block account writes until auth has finished and the cloud read for this
 * generation has settled. Otherwise an empty first paint overwrites the account.
 */
export function canWriteAccountFavorites(state: {
  authLoading: boolean;
  loadGeneration: number;
  settledGeneration: number;
}): boolean {
  if (state.authLoading) return false;
  return state.loadGeneration > 0 && state.settledGeneration === state.loadGeneration;
}
